import { reportSGM } from '@jdt-jinke/jdpay_utils'

/**
 * 验证数据对象中是否包含所有必需的键。
 * @param requireKeys 必需的键数组
 * @param data 待验证的数据对象
 * @param name 验证名称，用于错误报告
 * @returns 如果所有必需键都存在，返回true；否则返回false
 */
export function paramsValidate(
  requireKeys: string[],
  data: Record<string, any>,
  name?: string
): boolean {
  if (!requireKeys?.length) {
    requireKeys = Object.keys(data)
  }
  // 查找缺失的必需参数
  const invalidKey = requireKeys.find(
    (key) => !data?.[key] && typeof data?.[key] !== 'number'
  )

  // 如果存在缺失的必需参数，报告错误并返回false
  if (invalidKey && name) {
    reportSGM({
      code: `${name}_paramsVerify_fail`,
      type: 2,
      err: {},
      res: { requireKeys, data }
    })
    return false
  }

  // 所有必需参数都存在，返回true
  return true
}

/**
 * 获取 url 里的所有参数
 * */
export function getUrlParams(url?: string): Record<string, string> {
  // 如果没有提供 URL，则尝试使用当前页面的 URL
  const currentUrl =
    url || (typeof window !== 'undefined' ? window.location.href : '')

  const params: Record<string, string> = {}
  const [, paramsString] = currentUrl.split('?')

  if (!paramsString) return params

  paramsString.split('&').forEach((item) => {
    const [key, value] = item.split('=')
    if (typeof key === 'string' && typeof value === 'string') {
      // 重复参数告警
      if (key in params) {
        // TODO: 页面入参有相同的key
        reportSGM({
          type: 3,
          code: 'formatUrlParams_keyError',
          res: { url }
        })
      }
      // 直接覆盖参数值，后面的值会覆盖前面的值
      params[key] = decodeURIComponent(value)
    }
  })

  return params
}

// 格式化url
export function formatUrlParams(
  url?: string,
  params?: Record<string, any>
): string {
  if (!url) return (url = location.href)
  try {
    if (!params || Object.keys(params).length === 0) return url

    // 创建一个新的 URL 对象，以便在不影响原始 URL 的情况下操作参数
    const urlObj = new URL(url, window.location.origin) // 提供基础 URL 以支持相对路径
    Object.keys(params).forEach((key) => {
      const value = params[key]
      // 检查value是否为空，如果为空则不添加到URL参数中
      if (value === null || value === undefined || value === '') return
      if (typeof value === 'object') {
        // 对象类型的值转换为 JSON 字符串
        urlObj.searchParams.set(key, JSON.stringify(value))
      } else {
        // 其他类型的值直接添加
        urlObj.searchParams.set(key, String(value))
      }
    })
    return urlObj.toString()
  } catch (err) {
    // 这里可以添加错误处理逻辑
    reportSGM({
      code: 'formatUrlParams_error',
      err,
      res: { params, url }
    })
    return url // 发生错误时返回原始 URL
  }
}

/** 自动去除 url 里的 undefined 参数 */
export function queryPurify(
  query: Record<string, any>,
  sgmCode?: string
): Record<string, any> {
  const purifiedQuery: Record<string, any> = {}
  Object.keys(query).forEach((key) => {
    if (key && (query[key] || query[key] === false)) {
      purifiedQuery[key] = query[key]
    } else if (sgmCode) {
      reportSGM({
        type: 3,
        code: `queryPurify_${sgmCode || 'dataAbnormal'}`,
        req: query
      })
    }
  })
  return purifiedQuery
}

/** 删除url里的某些字段 */
export function getDeletedParamUrl(key: string, url?: string): string {
  // 如果没有提供 URL，则尝试使用当前页面的 URL
  const currentUrl =
    url || (typeof window !== 'undefined' ? window.location.href : '')
  if (!key || !currentUrl) return currentUrl

  // 使用 URLSearchParams 处理查询参数，更安全且易于理解
  const urlObj = new URL(currentUrl)
  urlObj.searchParams.delete(key)

  // 返回处理后的 URL 字符串
  return urlObj.toString()
}
